<template>
  <div id="app" class="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-200">
    <!-- Global Loading Overlay -->
    <div
      v-if="authStore.loading && !authStore.isInitialized"
      class="fixed inset-0 z-50 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm"
    >
      <div class="text-center">
        <div class="loading-spinner w-8 h-8 border-primary-600 mx-auto mb-4" />
        <p class="text-gray-600 dark:text-gray-400">Initializing application...</p>
      </div>
    </div>

    <!-- Session Timeout Warning -->
    <SessionTimeoutWarning
      v-if="authStore.sessionTimeoutWarning"
      @extend="handleExtendSession"
      @logout="handleLogout"
    />

    <!-- Notification System -->
    <NotificationContainer />

    <!-- Main Application -->
    <div v-if="authStore.isInitialized" class="min-h-screen">
      <!-- Navigation for authenticated users -->
      <Navigation v-if="authStore.isAuthenticated" />

      <!-- Main Content -->
      <main :class="authStore.isAuthenticated ? 'lg:pl-64' : ''">
        <RouterView v-slot="{ Component, route: currentRoute }">
          <transition :name="getTransitionName(currentRoute)" mode="out-in" appear>
            <component :is="Component" :key="currentRoute.path" />
          </transition>
        </RouterView>
      </main>

      <!-- Footer for unauthenticated pages -->
      <AppFooter v-if="!authStore.isAuthenticated" />
    </div>

    <!-- Error Boundary -->
    <div v-if="hasGlobalError" class="error-boundary">
      <div class="content">
        <h1 class="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-4">
          Something went wrong
        </h1>
        <p class="text-gray-600 dark:text-gray-400 mb-6">
          We're sorry, but something unexpected happened. Please try refreshing the page.
        </p>
        <button class="btn-primary" @click="handleRefresh">Refresh Page</button>
      </div>
    </div>

    <!-- PWA Update Available -->
    <PWAUpdatePrompt
      v-if="showPWAPrompt"
      @update="handlePWAUpdate"
      @dismiss="showPWAPrompt = false"
    />

    <!-- Development Debug Info -->
    <AuthDebugInfo />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { storageService } from '@/services/storage';

// Components
import Navigation from '@/components/layout/Navigation.vue';
import AppFooter from '@/components/layout/AppFooter.vue';
import SessionTimeoutWarning from '@/components/auth/SessionTimeoutWarning.vue';
import NotificationContainer from '@/components/ui/NotificationContainer.vue';
import PWAUpdatePrompt from '@/components/ui/PWAUpdatePrompt.vue';
import AuthDebugInfo from '@/components/dev/AuthDebugInfo.vue';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

// State
const hasGlobalError = ref(false);
const showPWAPrompt = ref(false);

// Theme management
const initializeTheme = () => {
  const theme = storageService.getTheme();
  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  document.documentElement.classList.toggle('dark', isDark);
};

// Route transition names
const getTransitionName = (route: any) => {
  if (route.meta?.transition) {
    return route.meta.transition;
  }

  // Default transitions based on route
  if (route.name === 'auth') {
    return 'fade';
  }

  return 'slide-fade';
};

// Session management
const handleExtendSession = async () => {
  try {
    const success = await authStore.refreshSession();
    if (!success) {
      await handleLogout();
    }
  } catch (error) {
    console.error('Failed to extend session:', error);
    await handleLogout();
  }
};

const handleLogout = async () => {
  try {
    await authStore.logout();
    await router.push({ name: 'auth' });
  } catch (error) {
    console.error('Logout error:', error);
  }
};

// Error handling
const handleRefresh = () => {
  window.location.reload();
};

// Global error handler
const handleGlobalError = (error: Error) => {
  console.error('Global application error:', error);
  hasGlobalError.value = true;
};

// PWA update handling
const handlePWAUpdate = () => {
  // This would be implemented with a service worker
  window.location.reload();
};

// Theme change handler
const handleThemeChange = (e: MediaQueryListEvent) => {
  const theme = storageService.getTheme();
  if (theme === 'system') {
    document.documentElement.classList.toggle('dark', e.matches);
  }
};

// Keyboard shortcuts
const handleKeyboardShortcuts = (e: KeyboardEvent) => {
  // Cmd/Ctrl + K for search
  if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
    e.preventDefault();
    // Trigger global search
    window.dispatchEvent(new CustomEvent('app:search'));
  }

  // Escape to close modals
  if (e.key === 'Escape') {
    window.dispatchEvent(new CustomEvent('app:escape'));
  }
};

// Lifecycle
onMounted(async () => {
  try {
    // Initialize theme first
    initializeTheme();

    // Set up theme change listener
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', handleThemeChange);

    // Add keyboard shortcuts
    document.addEventListener('keydown', handleKeyboardShortcuts);

    // Set up global error handling early
    window.addEventListener('error', (e: ErrorEvent) =>
      handleGlobalError(e.error || new Error(e.message))
    );
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) =>
      handleGlobalError(e.reason instanceof Error ? e.reason : new Error(String(e.reason)))
    );

    // Initialize auth store and wait for completion
    console.log('App.vue: Initializing auth store...');
    await authStore.initialize();
    console.log('App.vue: Auth store initialized, isAuthenticated:', authStore.isAuthenticated);

    // After auth initialization, handle intended route navigation
    if (authStore.isAuthenticated) {
      const intendedRoute = storageService.consumeIntendedRoute();
      if (intendedRoute) {
        console.log('App.vue: Navigating to intended route:', intendedRoute);
        await nextTick();
        await router.push(intendedRoute);
      }
    }

    // If auth token exists but authentication failed, don't clear it immediately
    // Let the user retry or handle it manually
    if (!authStore.isAuthenticated && storageService.hasAuthToken()) {
      console.log('App.vue: Auth token present but authentication not complete');
    }

    // Check for PWA update
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        showPWAPrompt.value = true;
      });
    }
  } catch (error) {
    console.error('App initialization error:', error);
    hasGlobalError.value = true;
  }
});

onUnmounted(() => {
  // Clean up event listeners
  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.removeEventListener('change', handleThemeChange);
  document.removeEventListener('keydown', handleKeyboardShortcuts);
  window.removeEventListener('error', (e: ErrorEvent) =>
    handleGlobalError(e.error || new Error(e.message))
  );
  window.removeEventListener('unhandledrejection', (e: PromiseRejectionEvent) =>
    handleGlobalError(e.reason instanceof Error ? e.reason : new Error(String(e.reason)))
  );
});

// Watch for route changes to update document title
watch(
  () => route.meta.title,
  title => {
    if (title) {
      document.title = `${title} | MCP Tools Collection`;
    } else {
      document.title = 'MCP Tools Collection | Profikid';
    }
  },
  { immediate: true }
);

// Watch for authentication state changes
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated, wasAuthenticated) => {
    console.log('Auth state changed:', isAuthenticated, 'was:', wasAuthenticated);

    if (!isAuthenticated && route.meta?.requiresAuth) {
      // Store current route as intended route before redirecting
      storageService.storeCurrentAsIntended({
        path: route.path,
        query: route.query,
        params: route.params,
      });
      router.push({ name: 'auth' });
    } else if (isAuthenticated && route.name === 'auth') {
      // If user becomes authenticated while on auth page, redirect
      const intendedRoute = storageService.consumeIntendedRoute();
      if (intendedRoute) {
        router.push(intendedRoute);
      } else {
        router.push({ name: 'dashboard' });
      }
    }
  }
);

// Watch for errors in auth store
watch(
  () => authStore.error,
  error => {
    if (error) {
      console.error('Auth error:', error);

      // Handle specific error types
      if (error.type === 'SESSION_EXPIRED') {
        // Automatically redirect to auth
        router.push({ name: 'auth' });
      }
    }
  }
);
</script>

<style scoped>
/* Route transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.3s ease-in;
}

.slide-fade-enter-from {
  transform: translateX(20px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-20px);
  opacity: 0;
}

/* Loading spinner */
.loading-spinner {
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Error boundary styling */
.error-boundary {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: theme('colors.gray.50');
}

.dark .error-boundary {
  background: theme('colors.gray.900');
}

.error-boundary .content {
  text-align: center;
  max-width: 28rem;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .fade-enter-active,
  .fade-leave-active,
  .slide-fade-enter-active,
  .slide-fade-leave-active {
    transition: none;
  }

  .loading-spinner {
    animation: none;
  }
}

/* Print styles */
@media print {
  .no-print {
    display: none !important;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .loading-spinner {
    border-top-color: currentColor;
    border-width: 3px;
  }
}
</style>
